import React, { useEffect, useState } from 'react';

const MAIN_RE = /\/static\/js\/main\.[A-Za-z0-9]+\.js/;
const POLL_MS = 5 * 60 * 1000;
const MIN_GAP_MS = 60 * 1000;

/** The main bundle path named in an index.html, or null when none is found. */
export function mainBundleOf(html: string): string | null {
  const m = /<script[^>]*\ssrc=["']([^"']*\/static\/js\/main\.[A-Za-z0-9]+\.js)["']/i.exec(html || '');
  if (!m) return null;
  const p = MAIN_RE.exec(m[1]);
  return p ? p[0] : null;
}

function loadedBundle(): string | null {
  const scripts = Array.from(document.getElementsByTagName('script'));
  for (const s of scripts) {
    const src = s.getAttribute('src') || '';
    const p = MAIN_RE.exec(src);
    if (p) return p[0];
  }
  return null;
}

/**
 * A tab left open across a deploy keeps running the old bundle. index.html is
 * served no-store, so comparing its main bundle with the one this tab loaded
 * tells us a newer build exists. Any failure stays silent.
 */
const NewVersionBanner: React.FC = () => {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const loaded = loadedBundle();
    if (!loaded) return undefined;
    let last = 0;
    let alive = true;

    const check = async () => {
      const now = Date.now();
      if (now - last < MIN_GAP_MS) return;
      last = now;
      try {
        const res = await fetch('/index.html', { cache: 'no-store' });
        if (!res.ok) return;
        const current = mainBundleOf(await res.text());
        if (alive && current && current !== loaded) setStale(true);
      } catch {
        /* offline or blocked: never a false alarm */
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const timer = window.setInterval(() => void check(), POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (!stale) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
        zIndex: 10000, display: 'flex', alignItems: 'center', gap: 12,
        maxWidth: 'calc(100vw - 32px)', padding: '10px 14px', borderRadius: 12,
        background: '#1f2937', color: '#fff', fontSize: 14,
        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.25)',
      }}
    >
      <span>A new version of eMerge is available. Reload to get the latest screens.</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{ padding: '4px 12px', borderRadius: 8, border: 0, background: '#fff', color: '#1f2937', fontWeight: 600, cursor: 'pointer' }}
      >
        Reload
      </button>
    </div>
  );
};

export default NewVersionBanner;
